import { Container, Divider, Link, MenuItem, MenuList, Popover, Theme, useMediaQuery } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import PaymentsIcon from '@mui/icons-material/Payments';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import HelpIcon from '@mui/icons-material/Help';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import logoutIconInverted from '../logoutIconInverted.svg';
import TuneIcon from '@mui/icons-material/Tune';
import { DateTime } from 'luxon';
import { PayoutReportItem, FileFormat, PayoutReport, TenantAccount } from '../gql-types.generated';
import { PayoutReportDisplayName } from '../util/PayoutReportDisplayName';
import PayoutReportDownloadOptions from './PayoutReportDownloadOptions';
import PayoutReportsHistory from './PayoutReportsHistory';
import { RoutePath } from '../util/Routes';
import { createPayoutReport } from '../features/home/HomeActions';
import {
  capturePayoutReport,
  capturePayoutReportCancelledInFlight,
  capturePayoutReportError,
  capturePayoutReportInFlight,
} from '../features/home/HomeSlice';
import { fetchIsTenantMenuOpen } from '../features/app/AppSlice';
import { ApteanSSOProvider } from '../util/ApteanSSOProvider';
import { AUTH_MODE } from '../util/Constants';
import { b2cAuthProvider } from '../util/AuthProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      textAlign: 'start',
    },
    userInfo: {
      padding: theme.spacing(3),
    },
    menuItem: {
      height: 55,
      textDecoration: 'none',
      color: theme.palette.text.primary,
      '&>.MuiDivider-root': {
        width: 'calc(100% - 12px)',
        position: 'absolute',
        top: 0,
        left: 0,
        marginLeft: 12,
      },
    },
    menuItemIcon: {
      marginRight: theme.spacing(2),
      color: theme.palette.uxGrey.main,
    },
    logoutIcon: {
      marginRight: theme.spacing(2),
      width: '1em',
      height: '1em',
      fontSize: '1.5rem',
      transform: 'rotate(180deg)',
    },
    manageAccountIcon: {
      marginRight: theme.spacing(2),
      width: '1em',
      height: '1em',
      fontSize: '1.5rem',
    },
    subMenuItem: {
      marginLeft: 32,
      paddingLeft: 32,
    },
    navList: {
      width: '100%',
      padding: 0,
    },
    paper: {
      textAlign: 'start',
      [theme.breakpoints.down('lg')]: {
        margin: theme.spacing(2, 0),
      },
      width: '100%',
    },
    reports: {},
    report: {
      paddingTop: 4,
      cursor: 'pointer',
    },
    contentWrap: {
      padding: 0,
    },
  }),
);
interface NavigationListProps {
  afterClick?: () => void;
  report?: PayoutReport;
  reportError?: Error;
  isFetchingPayoutUrl: boolean;
  tenantAccount?: TenantAccount;
  showStatements: boolean;
  showPayoutReports: boolean;
}

const NavigationList: React.FC<NavigationListProps> = props => {
  const { afterClick, report, reportError, isFetchingPayoutUrl, tenantAccount, showStatements, showPayoutReports } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [reportsOpen, setReportsOpen] = useState<boolean>(false);
  const [reportsHistoryVisible, setReportsHistoryVisible] = useState<boolean>(false);
  const [isReportMenuOpen, setIsReportMenuOpen] = useState(false);
  const [reportAnchorEl, setReportAnchorEl] = useState<HTMLAnchorElement | null>(null);
  const [selectedPayout, setSelectedPayout] = useState<string | undefined>(undefined);
  const [latestPayoutReports, setLatestPayoutReports] = useState<PayoutReportItem[]>([]);
  const [lastThreeReports, setLastThreeReports] = useState<(JSX.Element | null)[]>([]);
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
  const [customerFeatureEnabled, setCustomerFeatureEnabled] = useState<boolean>(false);
  const matchesMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [isUserManager] = useState<boolean>(ApteanSSOProvider.userPermissions().canManageUsers);
  const navItemClick = () => {
    if (afterClick) {
      afterClick();
    }
    if (matchesMdDown) {
      dispatch(fetchIsTenantMenuOpen(false));
    }
  };
  const clearPayoutReportOrError = () => {
    dispatch(capturePayoutReport(undefined));
    dispatch(capturePayoutReportError(undefined));
    dispatch(capturePayoutReportInFlight(false));
  };
  const handlePayoutReportDownload = (payoutReportId: string, format: FileFormat) => {
    if (payoutReportId) {
      dispatch(createPayoutReport(payoutReportId, format));
    }
  };
  const updatePayoutReportCancelledInFlight = (cancelledInFlight: boolean) => {
    dispatch(capturePayoutReportCancelledInFlight(cancelledInFlight));
  };
  const cancelPayoutReport = () => {
    if (isFetchingPayoutUrl) {
      updatePayoutReportCancelledInFlight(true);
    }
    clearPayoutReportOrError();
  };
  const payoutReportMenuOpen = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, payoutId: string) => {
    event.preventDefault();
    setReportAnchorEl(event.currentTarget);
    setIsReportMenuOpen(true);
    setSelectedPayout(payoutId);
    updatePayoutReportCancelledInFlight(false);
    clearPayoutReportOrError();
  };

  useEffect(() => {
    if (tenantAccount) {
      // Update is onboarded & customer feature enabled states.
      setIsOnboarded(!!tenantAccount.tosAcceptance);
      setCustomerFeatureEnabled(tenantAccount?.settings?.features?.customers?.enabled || false);
      // Update payout items.
      const { payoutReports } = tenantAccount;
      let latestReports =
        payoutReports &&
        payoutReports?.slice().sort((a: PayoutReportItem, b: PayoutReportItem) => {
          const aDate = DateTime.fromISO(a.endTime).toUTC();
          const bDate = DateTime.fromISO(b.endTime).toUTC();
          return aDate > bDate ? -1 : 1;
        });
      // Update the payout reports.
      if (latestReports && latestReports?.length > 0) {
        const recentDate = DateTime.fromISO(latestReports[0].endTime).toUTC();
        const recentMonth = recentDate.month;
        const recentYear = recentDate.year;
        latestReports = latestReports.filter((report: PayoutReportItem) => {
          const reportDate = DateTime.fromISO(report.endTime).toUTC();
          const reportMonth = reportDate.month;
          const reportYear = reportDate.year;
          if (reportYear < recentYear - 1) return false;
          let diff = recentMonth - reportMonth;
          diff = diff < 0 ? diff + 12 : diff;
          return diff >= 0 && diff <= 2;
        });
        setLatestPayoutReports(latestReports);

        const lastThreeReports = latestReports.slice(0, 3);
        setLastThreeReports(
          lastThreeReports.map((report, index) => {
            const reportId = report.payoutReportId;
            if (!reportId) return null;
            const name = PayoutReportDisplayName(report.startTime, report.endTime);
            return (
              <MenuItem
                className={`${classes.menuItem} ${classes.subMenuItem}`}
                onClick={event => payoutReportMenuOpen(event as unknown as React.MouseEvent<HTMLAnchorElement, MouseEvent>, reportId)}
                data-cy="payout-report"
                key={index}
              >
                <Divider />
                {name}
              </MenuItem>
            );
          }),
        );
      } else {
        setLatestPayoutReports([]);
        setLastThreeReports([]);
      }
    }
  }, [tenantAccount]);

  const openReportsHistory = () => {
    setReportsHistoryVisible(true);
    updatePayoutReportCancelledInFlight(false);
    clearPayoutReportOrError();
  };

  const closeReportsHistory = () => {
    setReportsHistoryVisible(false);
    clearPayoutReportOrError();
  };

  const payoutReportMenuClose = () => {
    if (isFetchingPayoutUrl) {
      updatePayoutReportCancelledInFlight(true);
    }
    setReportAnchorEl(null);
    setIsReportMenuOpen(false);
    setSelectedPayout(undefined);
    clearPayoutReportOrError();
  };
  const handleLogout = () => {
    if (AUTH_MODE === 'b2c') {
      b2cAuthProvider?.logout();
    } else {
      ApteanSSOProvider.exitPortalApp();
    }
  };
  let signOutText = 'Exit';
  if (AUTH_MODE === 'b2c') {
    signOutText = 'Sign out';
  }

  return (
    <Container className={classes.contentWrap}>
      <MenuList className={classes.navList} data-cy="settings-menu">
        <MenuItem component={Link} className={classes.menuItem} onClick={navItemClick} href={'/'} data-cy="nav-home">
          <HomeIcon className={classes.menuItemIcon} />
          Home
        </MenuItem>
        <MenuItem
          component={NavLink}
          className={classes.menuItem}
          onClick={navItemClick}
          to="/all-payments"
          data-cy="nav-all-payments"
        >
          <Divider />
          <PaymentsIcon className={classes.menuItemIcon} />
          Payments
        </MenuItem>
        <MenuItem
          component={NavLink}
          className={classes.menuItem}
          onClick={navItemClick}
          to="/payment-requests"
          data-cy="nav-payment-requests"
        >
          <Divider />
          <RequestQuoteIcon className={classes.menuItemIcon} />
          Payment Requests
        </MenuItem>
        {showStatements && (
          <MenuItem
            component={NavLink}
            className={classes.menuItem}
            onClick={navItemClick}
            to={RoutePath.Statements}
            data-cy="nav-statements"
          >
            <Divider />
            <FactCheckIcon className={classes.menuItemIcon} />
            Statements
          </MenuItem>
        )}
        {showPayoutReports && (
          <MenuItem className={classes.menuItem} onClick={() => setReportsOpen(!reportsOpen)} data-cy="nav-reports">
            <Divider />
            <AssessmentIcon className={classes.menuItemIcon} />
            Reports
          </MenuItem>
        )}
        {showPayoutReports &&
          reportsOpen && [
            lastThreeReports,
            <MenuItem
              key={3}
              className={`${classes.menuItem} ${classes.subMenuItem}`}
              onClick={lastThreeReports.length > 0 ? openReportsHistory : undefined}
              data-cy="view-all-statements"
            >
              <Divider />
              {lastThreeReports.length > 0 ? 'View All Reports' : 'No Reports Available'}
            </MenuItem>,
          ]}
        {isOnboarded && (
          <MenuItem component={NavLink} onClick={navItemClick} className={classes.menuItem} to="/settings" data-cy="manage-account">
            <Divider />
            <SettingsIcon className={classes.menuItemIcon} />
            Account Settings
          </MenuItem>
        )}
        {isOnboarded && (
          <MenuItem
            component={NavLink}
            onClick={navItemClick}
            className={classes.menuItem}
            to="/account-preferences"
            data-cy="account-preferences"
          >
            <Divider />
            <TuneIcon className={classes.menuItemIcon} />
            Account Preferences
          </MenuItem>
        )}
        {customerFeatureEnabled && (
          <MenuItem
            component={NavLink}
            onClick={navItemClick}
            className={classes.menuItem}
            to="/customer-management"
            data-cy="manage-customers"
          >
            <Divider />
            <PeopleIcon className={classes.menuItemIcon} />
            Manage Customers
          </MenuItem>
        )}
        {isOnboarded && isUserManager && (
          <MenuItem
            component={NavLink}
            onClick={navItemClick}
            className={classes.menuItem}
            to="/user-management"
            data-cy="manage-users"
          >
            <Divider />
            <PersonIcon className={classes.menuItemIcon} />
            Manage Users
          </MenuItem>
        )}
        <MenuItem component={NavLink} className={classes.menuItem} onClick={navItemClick} to="/help" data-cy="help">
          <Divider />
          <HelpIcon className={classes.menuItemIcon} />
          Help
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={handleLogout} data-cy="sign-out">
          <Divider />
          <img className={classes.logoutIcon} alt="logout" src={logoutIconInverted} />
          {signOutText}
        </MenuItem>
      </MenuList>
      {selectedPayout && (
        <Popover
          anchorEl={reportAnchorEl}
          open={isReportMenuOpen}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              payoutReportMenuClose();
            }
          }}
          disableEscapeKeyDown={isFetchingPayoutUrl}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <PayoutReportDownloadOptions
            isRecentPayout={true}
            handleCancel={payoutReportMenuClose}
            handleDownload={handlePayoutReportDownload}
            payoutId={selectedPayout}
            payoutUrl={report?.uri}
            payoutUrlError={reportError}
            isFetchingPayoutUrl={isFetchingPayoutUrl}
          />
        </Popover>
      )}
      <PayoutReportsHistory
        open={reportsHistoryVisible}
        close={closeReportsHistory}
        payoutReports={latestPayoutReports}
        payoutReport={report}
        payoutReportError={reportError}
        cancelPayoutReport={cancelPayoutReport}
        handleDownload={handlePayoutReportDownload}
        isFetchingPayoutUrl={isFetchingPayoutUrl}
      />
    </Container>
  );
};

export default NavigationList;
